import { ShowBase, useShowContext, useGetList } from "ra-core";
import { Link, useNavigate } from "react-router";
import {
  ArrowLeft,
  Phone,
  Mail,
  Briefcase,
  DollarSign,
  User,
  MapPin,
  Activity,
  Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { Sale, Job } from "../providers/fakerest/dataGenerator/types";
import { findStageLabel, findStageColor } from "../resources/jobs/jobStages";
import {
  getJobTypeLabel,
  getJobTypeBadgeClass,
  getJobTypeIcon,
} from "../resources/constants";

export const SalesShow = () => {
  return (
    <ShowBase resource="sales">
      <SalesShowContent />
    </ShowBase>
  );
};

const SalesShowContent = () => {
  const { record, isPending } = useShowContext<Sale>();

  if (isPending || !record) return null;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Back button */}
      <Link
        to="/jobs"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Jobs
      </Link>

      {/* Sales Rep Info Card */}
      <SalesInfoCard sale={record} />

      {/* Assigned Jobs */}
      <div className="mt-6">
        <SalesJobsList saleId={record.id} />
      </div>
    </div>
  );
};

const SalesInfoCard = ({ sale }: { sale: Sale }) => {
  const initials = `${sale.first_name?.[0] || ""}${sale.last_name?.[0] || ""}`;

  const roleColors: Record<string, string> = {
    admin: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    manager: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    sales_rep:
      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  };

  const roleLabels: Record<string, string> = {
    admin: "Administrator",
    manager: "Manager",
    sales_rep: "Sales Rep",
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage
              src={sale.avatar?.src}
              alt={`${sale.first_name} ${sale.last_name}`}
            />
            <AvatarFallback className="text-lg">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <CardTitle className="text-2xl">
              {sale.first_name} {sale.last_name}
            </CardTitle>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={roleColors[sale.role] || roleColors.sales_rep}>
                <Shield className="h-3 w-3 mr-1" />
                {roleLabels[sale.role] || "Sales Rep"}
              </Badge>
              {sale.disabled && (
                <Badge variant="outline" className="border-orange-500">
                  Disabled
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Contact details */}
        <div className="flex flex-wrap gap-4 text-sm">
          <a
            href={`mailto:${sale.email}`}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <Mail className="h-4 w-4" />
            {sale.email}
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

const SalesJobsList = ({ saleId }: { saleId: number }) => {
  const navigate = useNavigate();

  const { data: jobs, isPending } = useGetList<Job>("jobs", {
    filter: { assigned_to: saleId },
    sort: { field: "created_at", order: "DESC" },
    pagination: { page: 1, perPage: 100 },
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  const handleRowClick = (jobId: number) => {
    navigate(`/jobs/${jobId}/show`);
  };

  // Calculate stats
  const totalJobs = jobs?.length || 0;
  const totalValue =
    jobs?.reduce((sum, job) => sum + job.estimated_value, 0) || 0;
  const wonJobs = jobs?.filter((job) => job.status === "won").length || 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Assigned Jobs
          </CardTitle>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Total:</span>
              <span className="font-semibold">{totalJobs}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Won:</span>
              <span className="font-semibold text-green-600">{wonJobs}</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold">
                {formatCurrency(totalValue)}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : jobs && jobs.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      Address
                    </span>
                  </TableHead>
                  <TableHead>
                    <span className="flex items-center gap-1.5">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      Type
                    </span>
                  </TableHead>
                  <TableHead>
                    <span className="flex items-center gap-1.5">
                      <Activity className="h-4 w-4 text-muted-foreground" />
                      Status
                    </span>
                  </TableHead>
                  <TableHead className="text-right">
                    <span className="flex items-center gap-1.5 justify-end">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      Est. Value
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  const JobTypeIcon = getJobTypeIcon(job.job_type);
                  return (
                    <TableRow
                      key={job.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(job.id)}
                    >
                      <TableCell className="font-medium max-w-xs truncate">
                        {job.address}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${getJobTypeBadgeClass(job.job_type)} flex items-center gap-1.5 w-fit`}
                        >
                          <JobTypeIcon className="h-3 w-3" />
                          {getJobTypeLabel(job.job_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-2 h-2 rounded-full ${findStageColor(job.status)}`}
                          />
                          {findStageLabel(job.status)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(job.estimated_value)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm text-center py-4">
            No jobs assigned to this team member
          </p>
        )}
      </CardContent>
    </Card>
  );
};
